import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Input, Select, DatePicker, Checkbox, Button, Space, Radio,
  message, Typography, Tag, Spin, Divider, Table } from 'antd';
import { SaveOutlined, FilePdfOutlined, ArrowLeftOutlined, DeleteOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import useAvisoAlteracoes from '../hooks/useAvisoAlteracoes';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

const CHECKLIST = [
  ['cond_gerais', 'Condições gerais e estrutura do equipamento'],
  ['camara', 'Câmara de esterilização'],
  ['tampa', 'Tampa/porta e sistema de fechamento'],
  ['junta', 'Junta/vedação'],
  ['eletrico', 'Sistema elétrico e comandos'],
  ['aquecimento', 'Sistema de aquecimento e geração de vapor'],
  ['desaeracao', 'Desaeração/exaustão'],
  ['vazamentos', 'Ausência de vazamentos ou anormalidades aparentes'],
  ['funcionamento', 'Funcionamento geral do equipamento'],
];

const CNC = [{ value: 'C', label: 'C' }, { value: 'NC', label: 'NC' }, { value: 'NA', label: 'N/A' }];
const CNC2 = [{ value: 'C', label: 'Conforme' }, { value: 'NC', label: 'Não conforme' }];
const APROV = [{ value: 'APROVADO', label: 'Aprovado' }, { value: 'REPROVADO', label: 'Reprovado' }];

function Foto({ id, idx, aoRemover }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let vivo = true; let url = null;
    api.get('/manutencao-autoclave/' + id + '/fotos/' + idx, { responseType: 'blob' })
      .then((r) => { if (!vivo) return; url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => {});
    return () => { vivo = false; if (url) URL.revokeObjectURL(url); };
  }, [id, idx]);
  return (
    <div style={{ width: 150, marginRight: 10, marginBottom: 10 }}>
      <div style={{ border: '1px solid #d9d9d9', borderRadius: 4, height: 110, display: 'flex',
        alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: '#fafafa' }}>
        {src ? <img src={src} alt="" style={{ maxWidth: '100%', maxHeight: '100%' }} /> : <Spin size="small" />}
      </div>
      <Button size="small" danger block style={{ marginTop: 4 }} icon={<DeleteOutlined />}
        onClick={aoRemover}>remover</Button>
    </div>
  );
}

function ManutencaoAutoclave() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const naoSalvo = useAvisoAlteracoes(d);
  const [incubadoras, setIncubadoras] = useState([]);
  const [lotesBio, setLotesBio] = useState([]);
  const [lotesQui, setLotesQui] = useState([]);
  const [salvando, setSalvando] = useState(false);

  const set = (c, v) => setD((x) => ({ ...x, [c]: v }));
  const setChk = (k, campo, v) => setD((x) => ({
    ...x, checklist: { ...(x.checklist || {}), [k]: { ...((x.checklist || {})[k] || {}), [campo]: v } },
  }));

  const carregar = useCallback(async () => {
    try {
      const r = await api.get('/manutencao-autoclave/' + id);
      setD({ ...r.data, checklist: r.data.checklist || {} });
    } catch { message.error('Não foi possível abrir o laudo'); }
  }, [id]);

  useEffect(() => {
    carregar();
    api.get('/padroes-calibracao').then((r) => setIncubadoras((r.data || []).filter((p) => p.tipo === 'incubadora'))).catch(() => {});
    api.get('/indicadores-lotes?tipo=biologico').then((r) => setLotesBio(r.data || [])).catch(() => {});
    api.get('/indicadores-lotes?tipo=quimico').then((r) => setLotesQui(r.data || [])).catch(() => {});
  }, [carregar]);

  const salvar = async (depois) => {
    setSalvando(true);
    try {
      const r = await api.put('/manutencao-autoclave/' + id, d);
      setD({ ...r.data, checklist: r.data.checklist || {} });
      naoSalvo.limpo();
      message.success('Laudo salvo');
      if (depois) depois();
    } catch (e) { message.error('Erro ao salvar: ' + (e.response?.data?.erro || e.message)); }
    finally { setSalvando(false); }
  };

  const gerarPDF = () => salvar(async () => {
    message.loading({ content: 'Gerando PDF...', key: 'pdf', duration: 0 });
    try {
      const resp = await api.get('/manutencao-autoclave/' + id + '/pdf', { responseType: 'blob' });
      window.open(window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' })), '_blank');
      message.destroy('pdf');
      carregar();
    } catch { message.destroy('pdf'); message.error('Erro ao gerar PDF'); }
  });

  if (!d) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;

  const rotuloLote = (l) => (l.lote || '—') + (l.validade ? ' · val. ' + dayjs(String(l.validade).substring(0, 10)).format('DD/MM/YYYY') : '') +
    (l.fabricante ? ' · ' + l.fabricante : '');

  const parametro = (rot, campoValor, campoRes, ph) => (
    <Row gutter={8} style={{ marginBottom: 8 }} align="bottom">
      <Col span={6}><Text strong>{rot}</Text></Col>
      <Col span={8}>
        {campoValor
          ? <Input size="small" value={d[campoValor] || ''} placeholder={ph}
              onChange={(e) => set(campoValor, e.target.value)} />
          : <Text type="secondary">—</Text>}
      </Col>
      <Col span={10}>
        <Radio.Group size="small" optionType="button" options={CNC2}
          value={d[campoRes] || undefined} onChange={(e) => set(campoRes, e.target.value)} />
      </Col>
    </Row>
  );

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/laudos')}>Voltar</Button>
        <Title level={4} style={{ margin: 0 }}>Manutenção Preventiva de Autoclave</Title>
        {d.codigo_mpa ? <Tag color="blue">{d.codigo_mpa}</Tag> : <Tag>código a gerar</Tag>}
        {d.numero_os ? <Tag>OS {d.numero_os}</Tag> : null}
      </Space>

      <Card size="small" title="Identificação" style={{ marginBottom: 12 }}>
        <Row gutter={8}>
          <Col span={10}><Text type="secondary">Cliente</Text>
            <Input value={d.razao_social || d.nome_cliente || ''} disabled /></Col>
          <Col span={5}><Text type="secondary">Data da manutenção</Text>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
              value={d.data_manutencao ? dayjs(String(d.data_manutencao).substring(0, 10)) : null}
              onChange={(v) => set('data_manutencao', v ? v.format('YYYY-MM-DD') : null)} /></Col>
          <Col span={5}><Text type="secondary">Próxima manutenção</Text>
            <Input disabled value={d.data_vencimento ? dayjs(String(d.data_vencimento).substring(0, 10)).format('DD/MM/YYYY') : '—'} /></Col>
          <Col span={4} style={{ paddingTop: 22 }}>
            <Checkbox checked={!!d.usar_endereco_cnpj} onChange={(e) => set('usar_endereco_cnpj', e.target.checked)}>
              Usar endereço do CNPJ
            </Checkbox>
          </Col>
        </Row>
      </Card>

      <Card size="small" title="Dados do equipamento" style={{ marginBottom: 12 }}>
        <Row gutter={8}>
          <Col span={7}><Text type="secondary">Equipamento</Text>
            <Input value={d.equipamento || ''} onChange={(e) => set('equipamento', e.target.value)} /></Col>
          <Col span={4}><Text type="secondary">TAG</Text>
            <Input value={d.tag || ''} onChange={(e) => set('tag', (e.target.value || '').toUpperCase())} /></Col>
          <Col span={6}><Text type="secondary">Fabricante</Text>
            <Input value={d.fabricante || ''} onChange={(e) => set('fabricante', e.target.value)} /></Col>
          <Col span={7}><Text type="secondary">Modelo</Text>
            <Input value={d.modelo || ''} onChange={(e) => set('modelo', e.target.value)} /></Col>
          <Col span={7} style={{ marginTop: 8 }}><Text type="secondary">Nº de série</Text>
            <Input value={d.numero_serie || ''} onChange={(e) => set('numero_serie', e.target.value)} /></Col>
          <Col span={5} style={{ marginTop: 8 }}><Text type="secondary">Ano de fabricação</Text>
            <Input value={d.ano_fabricacao || ''} onChange={(e) => set('ano_fabricacao', e.target.value)} /></Col>
          <Col span={5} style={{ marginTop: 8 }}><Text type="secondary">Capacidade (L)</Text>
            <Input value={d.capacidade || ''} onChange={(e) => set('capacidade', e.target.value)} /></Col>
        </Row>
      </Card>

      <Card size="small" title="1. Inspeção visual e condições gerais" style={{ marginBottom: 12 }}>
        <Table rowKey={(r) => r[0]} size="small" pagination={false} dataSource={CHECKLIST}
          columns={[
            { title: 'Item de verificação', render: (r) => r[1] },
            { title: 'Resultado', width: 210, render: (r) => (
              <Radio.Group size="small" optionType="button" options={CNC}
                value={(d.checklist || {})[r[0]]?.v || undefined}
                onChange={(e) => setChk(r[0], 'v', e.target.value)} /> ) },
          ]} />
      </Card>

      <Card size="small" title="2. Parâmetros do ciclo de esterilização" style={{ marginBottom: 12 }}>
        <Row gutter={8} style={{ marginBottom: 12 }}>
          <Col span={12}><Text type="secondary">Ciclo realizado</Text>
            <Input value={d.ciclo_realizado || ''} onChange={(e) => set('ciclo_realizado', e.target.value)} /></Col>
          <Col span={12}><Text type="secondary">Tipo de carga</Text>
            <Input value={d.tipo_carga || ''} onChange={(e) => set('tipo_carga', e.target.value)} /></Col>
        </Row>
        <Divider style={{ margin: '8px 0' }} />
        {parametro('Desaeração', null, 'desaeracao_resultado')}
        {parametro('Temperatura', 'temperatura_valor', 'temperatura_resultado', '121 °C')}
        {parametro('Pressão', 'pressao_valor', 'pressao_resultado', '1,1 kgf/cm²')}
        {parametro('Tempo de esterilização', 'tempo_esterilizacao', 'tempo_esterilizacao_resultado', '20 min')}
        {parametro('Tempo de secagem', 'tempo_secagem', 'tempo_secagem_resultado', '15 min')}
      </Card>

      <Card size="small" title="3. Teste com indicadores" style={{ marginBottom: 12 }}>
        <Divider orientation="left" style={{ margin: '4px 0 10px' }}>3.1 Indicador biológico</Divider>
        <Row gutter={8}>
          <Col span={10}><Text type="secondary">Lote do indicador</Text>
            <Select style={{ width: '100%' }} allowClear showSearch optionFilterProp="label"
              value={d.ib_lote_id || undefined} onChange={(v) => set('ib_lote_id', v)}
              placeholder={lotesBio.length ? 'Escolha o lote' : 'Nenhum lote cadastrado'}
              options={lotesBio.map((l) => ({ value: l.id, label: rotuloLote(l) }))} /></Col>
          <Col span={7}><Text type="secondary">Incubadora utilizada</Text>
            <Select style={{ width: '100%' }} allowClear value={d.incubadora_id || undefined}
              onChange={(v) => set('incubadora_id', v)}
              placeholder={incubadoras.length ? 'Escolha' : 'Nenhuma cadastrada'}
              options={incubadoras.map((p) => ({ value: p.id, label: p.identificacao }))} /></Col>
          <Col span={7}><Text type="secondary">Resultado</Text>
            <Radio.Group style={{ display: 'block' }} optionType="button" options={APROV}
              value={d.ib_resultado || undefined} onChange={(e) => set('ib_resultado', e.target.value)} /></Col>
        </Row>
        <Row gutter={8} style={{ marginTop: 10 }}>
          <Col span={6}><Text type="secondary">Início da incubação</Text>
            <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }}
              value={d.ib_incubacao_inicio ? dayjs(d.ib_incubacao_inicio) : null}
              onChange={(v) => set('ib_incubacao_inicio', v ? v.toISOString() : null)} /></Col>
          <Col span={6}><Text type="secondary">Término da incubação</Text>
            <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%' }}
              value={d.ib_incubacao_fim ? dayjs(d.ib_incubacao_fim) : null}
              onChange={(v) => set('ib_incubacao_fim', v ? v.toISOString() : null)} /></Col>
          <Col span={12}><Text type="secondary">Controle positivo (indicador não processado, mesmo lote)</Text>
            <Radio.Group style={{ display: 'block' }} optionType="button"
              options={[{ value: 'CRESCEU', label: 'Houve crescimento — válido' },
                        { value: 'NAO_CRESCEU', label: 'Não houve — inválido' }]}
              value={d.ib_controle_positivo || undefined}
              onChange={(e) => set('ib_controle_positivo', e.target.value)} /></Col>
        </Row>

        <Divider orientation="left" style={{ margin: '16px 0 10px' }}>3.2 Indicador / integrador químico</Divider>
        <Row gutter={8}>
          <Col span={12}><Text type="secondary">Lote do indicador</Text>
            <Select style={{ width: '100%' }} allowClear showSearch optionFilterProp="label"
              value={d.iq_lote_id || undefined} onChange={(v) => set('iq_lote_id', v)}
              placeholder={lotesQui.length ? 'Escolha o lote' : 'Nenhum lote cadastrado'}
              options={lotesQui.map((l) => ({ value: l.id, label: rotuloLote(l) }))} /></Col>
          <Col span={12}><Text type="secondary">Resultado</Text>
            <Radio.Group style={{ display: 'block' }} optionType="button" options={APROV}
              value={d.iq_resultado || undefined} onChange={(e) => set('iq_resultado', e.target.value)} /></Col>
        </Row>
      </Card>

      <Card size="small" title="4. Registro fotográfico" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
          {(d.fotos || []).map((f, k) => (
            <Foto key={k} id={d.id} idx={k} aoRemover={async () => {
              try {
                const resp = await api.delete('/manutencao-autoclave/' + d.id + '/fotos/' + k);
                set('fotos', resp.data.fotos || []);
                message.success('Foto removida');
              } catch { message.error('Erro ao remover'); }
            }} />
          ))}
        </div>
        <input type="file" accept="image/*" multiple onChange={async (ev) => {
          const arquivos = Array.from(ev.target.files || []);
          if (!arquivos.length) return;
          message.loading({ content: 'Enviando foto(s)...', key: 'fotompa', duration: 0 });
          try {
            let ultimas = d.fotos || [];
            for (const f of arquivos) {
              const fd = new FormData();
              fd.append('foto', f);
              const resp = await api.post('/manutencao-autoclave/' + d.id + '/fotos', fd,
                { headers: { 'Content-Type': 'multipart/form-data' } });
              ultimas = resp.data.fotos || ultimas;
            }
            set('fotos', ultimas);
            message.success({ content: 'Foto(s) enviada(s)', key: 'fotompa' });
          } catch (e) {
            message.error({ content: 'Erro ao enviar: ' + (e.response?.data?.erro || e.message), key: 'fotompa' });
          }
          ev.target.value = '';
        }} />
        <Text type="secondary" style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
          Use para as fitas dos indicadores e o que mais precisar registrar.
        </Text>
      </Card>

      <Card size="small" title="5. Resultado da inspeção" style={{ marginBottom: 12 }}>
        <Radio.Group optionType="button" value={d.resultado || undefined}
          onChange={(e) => set('resultado', e.target.value)}
          options={[{ value: 'apto', label: 'Apto' },
                    { value: 'apto_com_observacoes', label: 'Apto com observações' },
                    { value: 'nao_apto', label: 'Não apto' }]} />
        <div style={{ marginTop: 10 }}>
          <Text type="secondary">Observações / não conformidades</Text>
          <TextArea rows={4} value={d.observacoes || ''} onChange={(e) => set('observacoes', e.target.value)}
            placeholder="Escreva aqui a análise dos resultados e as não conformidades encontradas" />
        </div>
      </Card>

      <Space style={{ marginBottom: 24 }}>
        <Button type="primary" icon={<SaveOutlined />} loading={salvando} onClick={() => salvar()}>Salvar</Button>
        <Button icon={<FilePdfOutlined />} style={{ color: '#cf1322', borderColor: '#cf1322' }}
          onClick={gerarPDF}>Salvar e gerar PDF</Button>
      </Space>
    </div>
  );
}

export default ManutencaoAutoclave;
